import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { User, UserRole } from '../types';
import { 
  SESSION_KEYS, 
  saveToSessionStorage, 
  loadFromSessionStorage, 
  removeFromSessionStorage,
  clearApplicationData,
  saveAuthToken,
  loadAuthToken,
  removeAuthToken
} from '../utils/sessionStorage';

interface AuthContextType {
  isAuthenticated: boolean;
  token: string | null;
  user: User | null;
  loading: boolean;
  error: string | null;
  login: (token: string) => void;
  logout: () => void;
  refreshUser: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

// JWT token payload interface
interface JWTPayload {
  sub: string;
  email: string;
  role: string;
  companyId?: string;
  iat: number;
  exp: number;
}

// Function to decode JWT token
const decodeJWT = (token: string): JWTPayload | null => {
  try {
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(atob(base64).split('').map(function(c) {
      return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
    }).join(''));
    return JSON.parse(jsonPayload);
  } catch (error) {
    console.error('Error decoding JWT token:', error);
    return null;
  }
};

// Function to check if token is expired
const isTokenExpired = (token: string): boolean => {
  const payload = decodeJWT(token);
  if (!payload) return true;
  
  const currentTime = Date.now() / 1000;
  return payload.exp < currentTime;
};

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);
  const [token, setToken] = useState<string | null>(null);
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const createUserFromToken = (token: string): User | null => {
    const payload = decodeJWT(token);
    if (!payload) return null;

    return {
      id: payload.sub,
      firstName: payload.email.split('@')[0], // Fallback - you might want to get this from API
      lastName: '', // Fallback - you might want to get this from API
      email: payload.email,
      role: payload.role as UserRole,
      status: 'active' as any, // Fallback
      companyId: payload.companyId,
      createdAt: new Date(),
      updatedAt: new Date(),
    };
  };

  const initializeAuth = () => {
    try {
      setLoading(true);
      const storedToken = loadAuthToken();
      
      if (!storedToken) {
        setIsAuthenticated(false);
        setToken(null);
        setUser(null);
        return;
      }

      // Check if token is expired
      if (isTokenExpired(storedToken)) {
        removeAuthToken();
        setIsAuthenticated(false);
        setToken(null);
        setUser(null);
        return;
      }

      // Token is valid, set authentication state
      const userData = createUserFromToken(storedToken);
      if (userData) {
        setIsAuthenticated(true);
        setToken(storedToken);
        setUser(userData);
      } else {
        // Invalid token format
        removeAuthToken();
        setIsAuthenticated(false);
        setToken(null);
        setUser(null);
      }
    } catch (err) {
      console.error('Error initializing auth:', err);
      removeAuthToken();
      setIsAuthenticated(false);
      setToken(null);
      setUser(null);
    } finally {
      setLoading(false);
    }
  };

  const login = (newToken: string) => {
    try {
      console.log('Login called with token:', newToken);
      console.log('Token type:', typeof newToken);
      console.log('Token has quotes:', newToken.startsWith('"') && newToken.endsWith('"'));
      
      // Remove quotes if they exist
      const cleanToken = newToken.startsWith('"') && newToken.endsWith('"') 
        ? newToken.slice(1, -1) 
        : newToken;
      
      console.log('Clean token:', cleanToken);
      
      // Validate token before storing
      const userData = createUserFromToken(cleanToken);
      if (!userData) {
        throw new Error('Invalid token format');
      }

      if (isTokenExpired(cleanToken)) {
        throw new Error('Token is expired');
      }

      // Store token in sessionStorage (without JSON serialization)
      saveAuthToken(cleanToken);
      
      // Update state
      setIsAuthenticated(true);
      setToken(cleanToken);
      setUser(userData);
      setError(null);
    } catch (err) {
      console.error('Error during login:', err);
      setError('Invalid authentication token');
      logout();
    }
  };

  const logout = () => {
    // Clear all application data from session storage
    clearApplicationData();
    removeAuthToken();
    
    // Reset state
    setIsAuthenticated(false);
    setToken(null);
    setUser(null);
    setError(null);
  };

  const refreshUser = async () => {
    // For now, we'll just re-validate the current token
    // In the future, you might want to call an API to get fresh user data
    if (token) {
      const userData = createUserFromToken(token);
      if (userData && !isTokenExpired(token)) {
        setUser(userData);
      } else {
        logout();
      }
    }
  };

  // Initialize authentication on mount
  useEffect(() => {
    initializeAuth();
  }, []);

  // Set up token expiration check
  useEffect(() => {
    if (!token) return;

    const checkTokenExpiration = () => {
      if (isTokenExpired(token)) {
        logout();
      }
    };

    // Check every minute
    const interval = setInterval(checkTokenExpiration, 60000);
    
    return () => clearInterval(interval);
  }, [token]);

  const value: AuthContextType = {
    isAuthenticated,
    token,
    user,
    loading,
    error,
    login,
    logout,
    refreshUser,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
